import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Phone, ChevronDown } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FASHION_FAQ_CATEGORIES, FASHION_GEO_LOCATIONS } from "@/data/fashion-faqs";

const fade = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

const CLIENTS = ["Payal Garg", "Sab Handpainted", "Shades by Vidhi", "Taiyariyan", "Studio Khaas", "Audarya Couture", "Gopal Kanda Label", "Callalily Atelier", "Mini Kicks", "Next Care Perfumes", "Farida Ajmal Perfumes"];

const SERVICES = [
  { cat: "Brand Positioning", items: ["Brand identity development", "Luxury positioning", "Collection launch strategy", "Visual identity consultation", "Market positioning"] },
  { cat: "Creative Production", items: ["Fashion photography direction", "Campaign shoots", "Editorial shoots", "Fashion films", "Lookbook creation", "Catalogue production", "Reels production", "Creative direction"] },
  { cat: "Digital Marketing", items: ["Instagram strategy", "Meta Ads", "Google Ads", "Pinterest strategy", "LinkedIn branding", "Influencer campaigns", "Community building"] },
  { cat: "E-Commerce", items: ["Shopify development", "Conversion optimization", "Product page optimization", "Customer journey mapping"] },
  { cat: "Growth Consulting", items: ["Go-to-market strategy", "International positioning", "Customer acquisition systems", "AI visibility for fashion brands"] }
];

export default function FashionPage() {
  const [openCat, setOpenCat] = useState(null);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* HERO */}
      <section className="pt-32 pb-20 px-6 md:px-16 lg:px-28">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] text-zinc-500 hover:text-white transition-colors mb-10 tracking-[0.2em]">
          <ArrowLeft className="w-3 h-3" /> Home
        </Link>
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-zinc-600 text-[10px] tracking-[0.4em] mb-10">Fashion & Lifestyle</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.08] max-w-5xl">
            Your designs are not the problem.
            <br /><span className="text-zinc-500">The way people experience your brand is.</span>
          </h1>
          <p className="text-zinc-400 text-sm mt-12 max-w-2xl leading-relaxed tracking-wider">
            If you're reading this, you've probably been told your designs are beautiful. And they probably are. But compliments don't pay rent. And the distance between "I love this" and "I'm buying this" is not about the product. It's about everything around the product — the photography, the website, the story, the pricing confidence, the feeling someone gets in the first 3 seconds of seeing your brand online.
          </p>
          <p className="text-zinc-500 text-sm mt-6 max-w-2xl leading-relaxed tracking-wider">
            We don't run your social media. We rebuild how your brand is experienced — from the inside out. Cash flow first. Then brand. Then growth. In that order.
          </p>
          <div className="flex flex-wrap gap-4 mt-12">
            <a href="https://wa.me/919041344420?text=Hi%20Ashish%2C%20I%20have%20a%20fashion%20brand%20and%20I%20think%20I%20need%20help%20figuring%20out%20what's%20not%20working." target="_blank" rel="noopener noreferrer"
              className="bg-white text-black px-8 py-4 text-xs tracking-[0.2em] hover:bg-zinc-200 transition-colors inline-flex items-center gap-3">
              Talk to Ashish directly <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </section>

      {/* THE REAL STORY */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <div className="max-w-3xl">
          <p className="text-zinc-600 text-[10px] tracking-[0.4em] mb-10">A real conversation. Not a pitch.</p>
          <h2 className="text-xl md:text-2xl tracking-tight leading-[1.2]">
            Ridima from Shades by Vidhi came to us saying she needed a social media team. She didn't.
          </h2>
          <div className="mt-10 space-y-8">
            <p className="text-zinc-400 text-sm tracking-wider leading-relaxed">
              She was spending on exhibitions. Running ads that weren't converting. Posting content that got no views. She was exhausted — not because she wasn't talented, but because she was doing everything and nothing was moving. That slow burn is what kills fashion businesses. Not the competition. The exhaustion of trying everything and watching nothing work.
            </p>
            <p className="text-zinc-400 text-sm tracking-wider leading-relaxed">
              When we sat down, I asked her the questions nobody had asked: where is your money actually going? What's your cash flow? Are you making new collections because you want to or because you feel you have to? Is that next exhibition really going to give you returns or is it just the only marketing you know?
            </p>
            <p className="text-zinc-400 text-sm tracking-wider leading-relaxed">
              She didn't need a social media team at 20-35 thousand a month. She could film and edit herself — her content was fine. What she needed was someone to sit with her business and restructure it. So I told her: take my consultancy instead. Don't spend on a team you're not ready for.
            </p>
            <p className="text-zinc-300 text-sm tracking-wider leading-relaxed">
              We started with cash flow. Not brand. Not ads. Cash flow. Once the numbers made sense, we rebuilt the website — 4-5 revisions, because brand owners rarely know what they want until they see what they don't want. Then we started growing. December: 67,000 in sales. January: 1 lakh. February: 1.5 lakhs. Then 2 lakhs. Then 2.4 lakhs. We're still growing.
            </p>
            <p className="text-zinc-500 text-sm tracking-wider leading-relaxed">
              Now we guide her on the minutest details — what hooks to use, what content calendar to follow, what products to launch, how to take her ideas from scratch to sales. It's not Prospera running her brand. It's Ridima telling me her ideas, and me reframing everything into a business structure that actually converts those ideas into revenue.
            </p>
          </div>
          <p className="text-zinc-600 text-xs mt-12 tracking-wider">
            That's how we work. Not services sold. Problems understood. Businesses rebuilt.
          </p>
        </div>
      </motion.section>

      {/* WHAT YOU'RE ACTUALLY FEELING */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-3xl leading-[1.15]">
          If any of this sounds like you, keep scrolling. We've been here before.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px mt-16 bg-white/10 max-w-5xl">
          {[
            { t: "Beautiful collections with zero visibility", d: "You invested lakhs in production. The fabrics are premium. The craftsmanship is exceptional. But your Instagram has 800 followers and your website gets 20 visitors a day. The collection exists in a vacuum." },
            { t: "Strong craftsmanship but weak positioning", d: "You're competing with labels half as skilled but twice as visible. They photograph better, they story-tell better, they position better. In fashion, perception creates demand before product quality does." },
            { t: "Low website conversions despite good traffic", d: "Visitors come but don't buy. The product pages lack persuasion. The brand story is buried. The checkout is complicated. The trust signals are missing. Your website is a brochure, not a boutique." },
            { t: "Overdependence on referrals and walk-ins", d: "Your best customers found you by accident. That's luck, not strategy. Without a digital acquisition system — SEO, ads, content, influencer marketing — growth is random and fragile." },
            { t: "Running ads without a brand narrative", d: "You're boosting posts and running generic ads. The ROAS is disappointing. Because ads without brand context are just noise. The ad needs to tell a story that makes the viewer feel something." },
            { t: "Difficulty commanding premium prices", d: "Friends say 'it's too expensive.' Customers compare you to Zara. That means your branding failed to communicate the difference. Premium pricing requires premium perception. Not just premium product." }
          ].map(s => (
            <div key={s.t} className="bg-black p-10">
              <p className="text-sm tracking-wider leading-relaxed">{s.t}</p>
              <p className="text-zinc-500 text-xs mt-4 leading-relaxed tracking-wider">{s.d}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* WHAT DO YOU ACTUALLY NEED? — Floating service diagnostic */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <h2 className="text-xl md:text-2xl tracking-tight max-w-3xl">What are you actually struggling with?</h2>
        <p className="text-zinc-500 text-xs mt-4 tracking-wider max-w-2xl leading-relaxed">
          Most fashion founders think they need social media. What they usually need is something upstream — positioning, pricing confidence, a website that doesn't leak customers, or sometimes just a plan. Click what resonates:
        </p>
        <div className="flex flex-wrap gap-3 mt-10">
          {[
            { label: "Nobody's buying despite good designs", to: "/services/brand-strategy" },
            { label: "My website gets traffic but no sales", to: "/services/digital-audit" },
            { label: "I need a brand identity that feels premium", to: "/services/brand-strategy" },
            { label: "My Instagram isn't growing", to: "/services/social-media-marketing" },
            { label: "I'm spending on ads but ROAS is terrible", to: "/services/meta-ads" },
            { label: "I need a fashion film or lookbook", to: "/services/brand-storytelling" },
            { label: "I want to sell internationally", to: "/services/content-marketing" },
            { label: "I need someone to guide me month by month", to: "/services/remote-cmo" },
            { label: "I need a website that actually converts", to: "/services/digital-audit" },
            { label: "I want AI and Google to recommend my brand", to: "/services/ai-search-optimization" },
            { label: "I need fashion photography direction", to: "/ad-studio" },
            { label: "I want my personal brand as a designer to grow", to: "/services/founder-branding" },
            { label: "I need help with collection launch strategy", to: "/services/brand-strategy" },
            { label: "I don't even know what I need — I just need direction", to: null }
          ].map(s => (
            s.to ? (
              <Link key={s.label} to={s.to}
                className="text-[11px] tracking-wider text-zinc-400 border border-white/15 px-5 py-3 hover:border-white/50 hover:text-white transition-colors">
                {s.label}
              </Link>
            ) : (
              <a key={s.label} href="https://wa.me/919041344420?text=Hi%20Ashish%2C%20I%20have%20a%20fashion%20brand%20and%20I%20honestly%20don't%20know%20what%20I%20need.%20Can%20we%20just%20talk%3F" target="_blank" rel="noopener noreferrer"
                className="text-[11px] tracking-wider text-white border border-white/40 px-5 py-3 hover:bg-white hover:text-black transition-colors">
                {s.label} →
              </a>
            )
          ))}
        </div>
      </motion.section>

      {/* BRANDS WE'VE WORKED WITH */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <h2 className="text-xl md:text-2xl tracking-tight">Brands that trusted us with their perception.</h2>
        <div className="flex flex-wrap gap-4 mt-10">
          {CLIENTS.map(c => <span key={c} className="text-xs text-zinc-400 tracking-wider border border-white/10 px-5 py-3">{c}</span>)}
        </div>
      </motion.section>

      {/* FAQ MEGA SECTION */}
      <section className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <motion.div variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <h2 className="text-2xl md:text-3xl tracking-tight max-w-3xl">Every question a fashion founder, designer, or brand owner has ever asked. Answered.</h2>
          <p className="text-zinc-500 text-xs mt-4 tracking-wider max-w-2xl leading-relaxed">
            {FASHION_FAQ_CATEGORIES.reduce((sum, cat) => sum + cat.faqs.length, 0)} questions across {FASHION_FAQ_CATEGORIES.length} categories. Built from real conversations with fashion founders, research across Reddit, Google, and AI platforms. If your question isn't here, it should be — message us and we'll add it.
          </p>
        </motion.div>

        {/* Category Chips */}
        <div className="flex flex-wrap gap-2 mt-10">
          <button onClick={() => setOpenCat(null)}
            className={`text-[10px] tracking-wider px-4 py-2 border transition-colors ${openCat === null ? "bg-white text-black border-white" : "border-white/20 text-zinc-400 hover:border-white/50"}`}>
            All categories
          </button>
          {FASHION_FAQ_CATEGORIES.map(cat => (
            <button key={cat.id} onClick={() => setOpenCat(openCat === cat.id ? null : cat.id)}
              className={`text-[10px] tracking-wider px-4 py-2 border transition-colors ${openCat === cat.id ? "bg-white text-black border-white" : "border-white/20 text-zinc-400 hover:border-white/50"}`}>
              {cat.label} ({cat.faqs.length})
            </button>
          ))}
        </div>

        {/* FAQ Accordions */}
        <div className="mt-12 max-w-4xl space-y-12">
          {FASHION_FAQ_CATEGORIES
            .filter(cat => openCat === null || openCat === cat.id)
            .map(cat => (
            <div key={cat.id}>
              <p className="text-[10px] tracking-[0.3em] text-zinc-600 mb-6">{cat.label}</p>
              <Accordion type="single" collapsible>
                {cat.faqs.map((f, i) => (
                  <AccordionItem key={`${cat.id}-${i}`} value={`${cat.id}-${i}`} className="border-white/10">
                    <AccordionTrigger className="text-left text-white hover:no-underline text-xs tracking-wider py-5">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-zinc-400 text-[10px] leading-relaxed tracking-wider pb-5">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      {/* WHERE WE SERVE — GEO DEEP */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <h2 className="text-xl md:text-2xl tracking-tight">Fashion perception engineering. Wherever fashion lives.</h2>
        <p className="text-zinc-500 text-xs mt-4 tracking-wider max-w-2xl leading-relaxed">
          From the designer studios in Shahpur Jat to the luxury boutiques on Via Montenapoleone. From Chandigarh's Sector 17 market to Dubai's D3 design district. We serve fashion businesses wherever they operate.
        </p>
        <div className="mt-12 space-y-8">
          <div>
            <p className="text-[10px] tracking-[0.3em] text-zinc-600 mb-4">India — deep local reach</p>
            <div className="flex flex-wrap gap-2">
              {[...FASHION_GEO_LOCATIONS.india.chandigarh, ...FASHION_GEO_LOCATIONS.india.delhi, ...FASHION_GEO_LOCATIONS.india.mumbai, ...FASHION_GEO_LOCATIONS.india.jaipur, ...FASHION_GEO_LOCATIONS.india.bangalore, ...FASHION_GEO_LOCATIONS.india.others].map(l =>
                <span key={l} className="text-[10px] text-zinc-500 border border-white/5 px-3 py-1 tracking-wider">{l}</span>
              )}
            </div>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.3em] text-zinc-600 mb-4">Middle East</p>
            <div className="flex flex-wrap gap-2">
              {FASHION_GEO_LOCATIONS.uae.map(l => <span key={l} className="text-[10px] text-zinc-500 border border-white/5 px-3 py-1 tracking-wider">{l}</span>)}
            </div>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.3em] text-zinc-600 mb-4">North America</p>
            <div className="flex flex-wrap gap-2">
              {FASHION_GEO_LOCATIONS.north_america.map(l => <span key={l} className="text-[10px] text-zinc-500 border border-white/5 px-3 py-1 tracking-wider">{l}</span>)}
            </div>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.3em] text-zinc-600 mb-4">Europe</p>
            <div className="flex flex-wrap gap-2">
              {FASHION_GEO_LOCATIONS.europe.map(l => <span key={l} className="text-[10px] text-zinc-500 border border-white/5 px-3 py-1 tracking-wider">{l}</span>)}
            </div>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.3em] text-zinc-600 mb-4">Asia Pacific</p>
            <div className="flex flex-wrap gap-2">
              {FASHION_GEO_LOCATIONS.other.map(l => <span key={l} className="text-[10px] text-zinc-500 border border-white/5 px-3 py-1 tracking-wider">{l}</span>)}
            </div>
          </div>
        </div>
      </motion.section>

      {/* ENTRY POINT — Consultancy */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <div className="max-w-3xl">
          <h2 className="text-2xl md:text-3xl tracking-tight leading-[1.15]">
            Not sure what you need? Start with a conversation.
          </h2>
          <p className="text-zinc-400 text-sm mt-8 tracking-wider leading-relaxed">
            Most fashion founders who come to us think they need social media. Or ads. Or a website. Usually they need something they haven't even considered yet — because the real problem is upstream of the symptom they're feeling.
          </p>
          <p className="text-zinc-400 text-sm mt-6 tracking-wider leading-relaxed">
            That's why our most popular entry point is a monthly growth consultancy. No long-term contracts. No massive commitments. Ashish works directly with you — analyzing your cash flow, your brand positioning, your digital presence, your content, your pricing, your customer journey. Every week, you get clarity on what to do next. Not generic advice. Specific actions for your brand, your market, your stage.
          </p>
          <p className="text-zinc-300 text-sm mt-6 tracking-wider leading-relaxed">
            Designers who started with consultancy went from confused to growing 2-4 lakhs monthly within months. Not because we ran their ads. Because we helped them understand what was actually broken and fixed it in the right order.
          </p>
        </div>
      </motion.section>

      {/* CTA */}
      <section className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <motion.div variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-3xl">
          <h2 className="text-2xl md:text-3xl tracking-tight leading-[1.15]">
            Your designs deserve to be desired, not just admired. Let's figure out what's actually in the way.
          </h2>
          <div className="flex flex-wrap gap-4 mt-12">
            <a href="https://wa.me/919041344420?text=Hi%20Ashish%2C%20I%20have%20a%20fashion%20brand%20and%20I%20want%20to%20understand%20what's%20actually%20not%20working.%20Can%20we%20talk%3F" target="_blank" rel="noopener noreferrer"
              className="bg-white text-black px-8 py-4 text-xs tracking-[0.2em] hover:bg-zinc-200 transition-colors inline-flex items-center gap-3">
              Talk to Ashish <ArrowRight className="w-4 h-4" />
            </a>
            <a href="tel:+916239547599" className="text-zinc-500 hover:text-white text-xs tracking-wider transition-colors inline-flex items-center gap-2 px-4">
              <Phone className="w-3 h-3" /> +91 6239 547 599
            </a>
          </div>
        </motion.div>
      </section>

      {/* SEO KEYWORDS */}
      <section className="py-12 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <div className="flex flex-wrap gap-2">
          {[
            "fashion marketing agency India", "fashion brand positioning", "luxury fashion marketing",
            "bridal wear marketing agency", "ethnic wear brand strategy", "fashion photography agency",
            "fashion brand not selling why", "fashion website not converting", "how to sell fashion online",
            "fashion SEO agency", "fashion brand storytelling", "lookbook production agency",
            "fashion film production", "fashion influencer marketing", "luxury brand positioning India",
            "fashion brand consulting", "D2C fashion marketing", "fashion e-commerce agency",
            "Shopify fashion store", "fashion collection launch strategy",
            "fashion marketing agency Chandigarh", "fashion marketing agency Delhi",
            "fashion marketing agency Mumbai", "fashion marketing agency Dubai",
            "fashion marketing agency New York", "fashion marketing agency Toronto",
            "fashion marketing agency London", "fashion marketing agency Jaipur",
            "fashion brand perception engineering", "couture marketing agency",
            "designer label marketing", "fashion AI visibility", "fashion GEO optimization",
            "how to price luxury fashion", "fashion brand identity design",
            "fashion trade show marketing", "fashion week promotion agency",
            "Indian fashion international expansion", "artisan fashion brand marketing",
            "handcrafted fashion marketing", "sustainable fashion marketing agency",
            "hire fashion marketing consultant", "fashion brand growth strategy",
            "why my fashion designs don't sell", "fashion brand visibility problem",
            "fashion brand conversion optimization", "fashion founder branding"
          ].map(kw => <span key={kw} className="text-[10px] text-zinc-800 border border-white/5 px-3 py-1 tracking-wider">{kw}</span>)}
        </div>
      </section>
    </div>
  );
}
